/**
 * LiveLogs
 *
 * Terminal-style streaming log viewer with auto-scroll.
 * Displays color-coded log entries based on log level.
 */

import { useRef, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';

/**
 * Log entry type with level, message, and optional metadata
 */
export interface LogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  metadata: Record<string, unknown> | null;
}

export interface LiveLogsProps {
  logs: LogEntry[];
  autoScroll: boolean;
  maxLines: number | null;
  style: CSSProperties | null;
}

/**
 * Map log level to color token
 */
function getLevelColor(level: LogEntry['level']): string {
  switch (level) {
    case 'debug':
      return tokens.colors.textMuted;
    case 'info':
      return tokens.colors.mint;
    case 'warn':
      return tokens.colors.warning;
    case 'error':
      return tokens.colors.danger;
    default:
      return tokens.colors.textSecondary;
  }
}

/**
 * Format log level to fixed-width label
 */
function formatLevel(level: LogEntry['level']): string {
  return level.toUpperCase().padEnd(5);
}

/**
 * Format timestamp for display (HH:MM:SS.mmm)
 */
function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  const ms = date.getMilliseconds().toString().padStart(3, '0');
  return `${hours}:${minutes}:${seconds}.${ms}`;
}

/**
 * Format metadata object to inline string
 */
function formatMetadata(metadata: Record<string, unknown> | null): string {
  if (metadata === null || Object.keys(metadata).length === 0) {
    return '';
  }
  return ` ${JSON.stringify(metadata)}`;
}

export function LiveLogs({ logs, autoScroll, maxLines, style }: LiveLogsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Apply maxLines limit if specified
  const displayLogs = maxLines !== null ? logs.slice(-maxLines) : logs;

  const containerStyle: CSSProperties = {
    background: tokens.colors.void,
    border: `1px solid ${tokens.colors.border}`,
    borderRadius: tokens.radii.md,
    padding: tokens.spacing[3],
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.sm,
    lineHeight: 1.6,
    overflow: 'auto',
    maxHeight: '400px',
    minHeight: '200px',
    ...style,
  };

  const logLineStyle: CSSProperties = {
    display: 'flex',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    padding: `${tokens.spacing[0.5]} 0`,
  };

  const timestampStyle: CSSProperties = {
    color: tokens.colors.textMuted,
    marginRight: tokens.spacing[2],
    flexShrink: 0,
  };

  const levelBaseStyle: CSSProperties = {
    marginRight: tokens.spacing[2],
    flexShrink: 0,
    fontWeight: tokens.fontWeights.medium,
  };

  const messageStyle: CSSProperties = {
    color: tokens.colors.textSecondary,
    flex: 1,
  };

  const metadataStyle: CSSProperties = {
    color: tokens.colors.textMuted,
    opacity: 0.7,
  };

  const placeholderStyle: CSSProperties = {
    color: tokens.colors.textMuted,
    fontStyle: 'italic',
    padding: tokens.spacing[4],
    textAlign: 'center',
  };

  if (displayLogs.length === 0) {
    return (
      <div ref={containerRef} style={containerStyle}>
        <div style={placeholderStyle}>Waiting for logs...</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={containerStyle}>
      {displayLogs.map((log, index) => (
        <div key={`${log.timestamp}-${index}`} style={logLineStyle}>
          <span style={timestampStyle}>{formatTimestamp(log.timestamp)}</span>
          <span style={{ ...levelBaseStyle, color: getLevelColor(log.level) }}>
            [{formatLevel(log.level)}]
          </span>
          <span style={messageStyle}>
            {log.message}
            {log.metadata && <span style={metadataStyle}>{formatMetadata(log.metadata)}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
